import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Inbox, Loader2, LogOut, Search } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["New", "Contacted", "In Progress", "Closed"];
const STATUS_STYLES = {
  New: "bg-coral/10 text-coral",
  Contacted: "bg-gold/20 text-[#9a7414]",
  "In Progress": "bg-turquoise/10 text-emeraldsea",
  Closed: "bg-ocean/10 text-ocean",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState(null);
  const [search, setSearch] = useState("");

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem("ato_token")}` });

  useEffect(() => {
    axios
      .get(`${API}/enquiries`, { headers: headers(), withCredentials: true })
      .then((res) => setEnquiries(res.data))
      .catch(() => navigate("/admin"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) { /* session may already be gone */ }
    localStorage.removeItem("ato_token");
    navigate("/admin");
  };

  const updateStatus = async (id, status) => {
    const { data } = await axios.patch(`${API}/enquiries/${id}`, { status }, { headers: headers(), withCredentials: true });
    setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
  };

  const visible = useMemo(() => {
    if (!enquiries) return [];
    const q = search.toLowerCase();
    return enquiries.filter((e) =>
      [e.full_name, e.email, e.phone, e.service, e.destination]
        .join(" ").toLowerCase().includes(q)
    );
  }, [enquiries, search]);

  const newCount = enquiries?.filter((e) => e.status === "New").length ?? 0;

  return (
    <div className="min-h-screen bg-sandlight">
      <header className="sticky top-0 z-40 border-b border-ocean/10 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" aria-label="Back to website"><Logo /></Link>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full bg-ocean px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-coral"
          >
            <LogOut className="h-3.5 w-3.5" /> Log Out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Travel Enquiries</h1>
            <p className="mt-1 text-sm text-mutedink">
              {enquiries ? `${enquiries.length} total · ${newCount} new` : "Loading enquiries..."}
            </p>
          </div>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-mutedink" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-search-input"
              placeholder="Search name, service, destination..."
              className="w-72 rounded-full border border-ocean/15 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-turquoise focus:ring-2 focus:ring-turquoise/30"
            />
          </div>
        </div>

        {enquiries === null ? (
          <div className="mt-20 flex justify-center text-turquoise">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : visible.length === 0 ? (
          <div className="mt-16 rounded-3xl bg-white p-14 text-center ring-1 ring-ocean/5">
            <Inbox className="mx-auto h-12 w-12 text-turquoise/50" />
            <p className="mt-4 font-display text-lg font-bold text-ink">No enquiries yet</p>
            <p className="mt-1 text-sm text-mutedink">
              New enquiries from the website form will appear here.
            </p>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-3xl bg-white ring-1 ring-ocean/5">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-ocean/10 text-xs uppercase tracking-wider text-mutedink">
                  <th className="px-5 py-4">Date</th>
                  <th className="px-5 py-4">Name</th>
                  <th className="px-5 py-4">Contact</th>
                  <th className="px-5 py-4">Service</th>
                  <th className="px-5 py-4">Trip Details</th>
                  <th className="px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => (
                  <tr key={e.id} data-testid="admin-enquiry-row" className="border-b border-ocean/5 align-top last:border-0 hover:bg-sandlight/60">
                    <td className="whitespace-nowrap px-5 py-4 text-xs text-mutedink">
                      {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </td>
                    <td className="px-5 py-4 font-semibold text-ink">{e.full_name}</td>
                    <td className="px-5 py-4 text-xs text-mutedink">
                      <div>{e.email}</div>
                      <div className="mt-0.5">{e.phone}</div>
                    </td>
                    <td className="px-5 py-4 text-xs font-semibold text-ocean">{e.service}</td>
                    <td className="max-w-[260px] px-5 py-4 text-xs text-mutedink">
                      {e.destination && <div><span className="font-semibold text-ink">To:</span> {e.destination}</div>}
                      {e.travel_dates && <div className="mt-0.5"><span className="font-semibold text-ink">Dates:</span> {e.travel_dates}</div>}
                      {e.travellers && <div className="mt-0.5"><span className="font-semibold text-ink">Travellers:</span> {e.travellers}</div>}
                      {e.budget && <div className="mt-0.5"><span className="font-semibold text-ink">Budget:</span> {e.budget}</div>}
                      {e.requirements && <div className="mt-1 italic">"{e.requirements}"</div>}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={e.status}
                        onChange={(ev) => updateStatus(e.id, ev.target.value)}
                        data-testid={`enquiry-status-select-${e.id}`}
                        aria-label={`Status for enquiry from ${e.full_name}`}
                        className={`cursor-pointer rounded-full border-0 px-3 py-1.5 text-xs font-semibold outline-none ${STATUS_STYLES[e.status]}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
